import * as ort from "onnxruntime-web";

const sessionCache = new Map<string, ort.InferenceSession>();
const VAD_HIDDEN_SIZE = 64;
let vadH: Float32Array = new Float32Array(2 * 1 * VAD_HIDDEN_SIZE);
let vadC: Float32Array = new Float32Array(2 * 1 * VAD_HIDDEN_SIZE);

export async function loadModel(path: string): Promise<ort.InferenceSession> {
  if (sessionCache.has(path)) {
    return sessionCache.get(path)!;
  }

  let session: ort.InferenceSession;
  try {
    session = await ort.InferenceSession.create(path, {
      executionProviders: ["webgpu", "wasm"],
    });
  } catch {
    session = await ort.InferenceSession.create(path, {
      executionProviders: ["wasm"],
    });
  }

  // Reset VAD state on every model load so a new exam session starts clean
  if (path.includes("silero_vad")) {
    vadH = new Float32Array(2 * 1 * VAD_HIDDEN_SIZE);
    vadC = new Float32Array(2 * 1 * VAD_HIDDEN_SIZE);
  }

  sessionCache.set(path, session);
  return session;
}

export async function runVad(
  session: ort.InferenceSession,
  frame: Float32Array
): Promise<number> {
  if (frame.length !== 512) {
    throw new RangeError(`runVad: frame must be 512 samples, got ${frame.length}`);
  }

  // Reshape to [1, 1, 512]
  const inputTensor = new ort.Tensor("float32", frame, [1, 1, 512]);

  // Sample rate tensor (int64)
  const srData = BigInt64Array.from([BigInt(16000)]);
  const srTensor = new ort.Tensor("int64", srData, [1]);

  // LSTM state tensors [2, 1, 64]
  const hTensor = new ort.Tensor("float32", vadH, [2, 1, VAD_HIDDEN_SIZE]);
  const cTensor = new ort.Tensor("float32", vadC, [2, 1, VAD_HIDDEN_SIZE]);

  const feeds: Record<string, ort.Tensor> = {
    input: inputTensor,
    sr: srTensor,
    h: hTensor,
    c: cTensor,
  };

  const results = await session.run(feeds);

  // Update persistent LSTM state
  if (results["hn"]) vadH = results["hn"].data as Float32Array;
  if (results["cn"]) vadC = results["cn"].data as Float32Array;

  // Speech probability is the first (and only) value of the output tensor
  const outputData = results["output"].data as Float32Array;
  return outputData[0];
}

export function resetVadState(): void {
  vadH = new Float32Array(2 * 1 * VAD_HIDDEN_SIZE);
  vadC = new Float32Array(2 * 1 * VAD_HIDDEN_SIZE);
}
export async function runEmbedding(
  session: ort.InferenceSession,
  samples: Float32Array
): Promise<Float32Array> {
  // Mean-normalise the input
  let mean = 0;
  for (let i = 0; i < samples.length; i++) mean += samples[i];
  mean /= samples.length;

  const normalised = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    normalised[i] = samples[i] - mean;
  }

  // Input shape [1, T] — batch size 1, T samples
  const inputTensor = new ort.Tensor("float32", normalised, [1, samples.length]);

  const results = await session.run({ input: inputTensor });

  const outputData = results["output"].data as Float32Array;
  return outputData;
}
